import { useState, useEffect, useRef, useCallback } from 'react';

// Define types for the Web Speech API since they might not be in the standard TS lib
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
}

// Window interface extension to support webkit prefix
interface WindowWithSpeech extends Window {
  SpeechRecognition?: new () => SpeechRecognition;
  webkitSpeechRecognition?: new () => SpeechRecognition;
}

export interface UseVoiceInputOptions {
  onAutoSend?: (transcript: string) => void;
  autoSendDelay?: number; // in milliseconds, default 5000 (5 seconds)
}

export interface UseVoiceInputReturn {
  isListening: boolean;
  transcript: string;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
  error: string | null;
}

export const useVoiceInput = (options: UseVoiceInputOptions = {}): UseVoiceInputReturn => {
  const { onAutoSend, autoSendDelay = 2000 } = options; // 2 second silence before auto-send
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const autoSendTimerRef = useRef<NodeJS.Timeout | null>(null);
  const transcriptRef = useRef<string>('');
  const shouldKeepListeningRef = useRef<boolean>(false); // Track if we should auto-restart

  // Keep ref in sync with transcript state
  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  const clearAutoSendTimer = useCallback(() => {
    if (autoSendTimerRef.current) {
      clearTimeout(autoSendTimerRef.current);
      autoSendTimerRef.current = null;
    }
  }, []);

  const startAutoSendTimer = useCallback(() => {
    clearAutoSendTimer();
    if (onAutoSend) {
      autoSendTimerRef.current = setTimeout(() => {
        // Use ref to get current transcript value at timer fire time
        const currentTranscript = transcriptRef.current.trim();
        // Send if we have any meaningful text (at least 1 word with 2+ chars)
        if (currentTranscript && currentTranscript.length >= 2) {
          onAutoSend(currentTranscript);
          // Clear transcript and restart recognition to flush the internal
          // SpeechRecognition results buffer. Without restarting, event.results
          // still contains all previous utterances and onresult will rebuild
          // the full transcript including already-sent text.
          transcriptRef.current = '';
          setTranscript('');
          if (recognitionRef.current && shouldKeepListeningRef.current) {
            try {
              recognitionRef.current.stop();
              // onend handler will auto-restart since shouldKeepListeningRef is true
            } catch {
              // ignore if already stopped
            }
          }
        }
      }, autoSendDelay);
    }
  }, [autoSendDelay, onAutoSend, clearAutoSendTimer]);

  useEffect(() => {
    const win = window as WindowWithSpeech;
    const SpeechRecognitionConstructor = win.SpeechRecognition || win.webkitSpeechRecognition;

    if (SpeechRecognitionConstructor) {
      setIsSupported(true);
      const recognition = new SpeechRecognitionConstructor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognitionRef.current = recognition;
    } else {
      setIsSupported(false);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Cleanup auto-send timer on unmount
  useEffect(() => {
    return () => {
      clearAutoSendTimer();
    };
  }, [clearAutoSendTimer]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current || !isSupported) return;

    try {
      // Reset error state on new attempt
      setError(null);

      // Mark that we want to keep listening (for auto-restart after send)
      shouldKeepListeningRef.current = true;

      // If already listening, don't start again
      if (isListening) return;

      const recognition = recognitionRef.current;

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let fullTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          fullTranscript += event.results[i][0].transcript;
        }
        setTranscript(fullTranscript);

        // Reset auto-send timer on new speech (5 seconds of silence triggers send)
        if (fullTranscript.trim()) {
          startAutoSendTimer();
        }
      };

      // Track consecutive errors via ref so it persists across re-renders
      const errorCountRef = { current: 0 };
      const MAX_ERRORS = 3;

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        // Don't log noise — aborted and no-speech are expected
        if (event.error !== 'aborted' && event.error !== 'no-speech') {
          console.error('Speech recognition error', event.error);
        }
        setError(event.error);

        // Fatal errors — stop immediately
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed' ||
            event.error === 'audio-capture' || event.error === 'network') {
          shouldKeepListeningRef.current = false;
          setIsListening(false);
          return;
        }

        errorCountRef.current++;
        if (errorCountRef.current >= MAX_ERRORS) {
          shouldKeepListeningRef.current = false;
          setIsListening(false);
        }
      };

      recognition.onend = () => {
        clearAutoSendTimer();
        setTranscript('');

        if (shouldKeepListeningRef.current && recognitionRef.current) {
          const errors = errorCountRef.current;
          const delay = errors > 0 ? Math.min(2000 * errors, 10000) : 300;
          setTimeout(() => {
            if (shouldKeepListeningRef.current && recognitionRef.current) {
              try {
                recognitionRef.current.start();
                setIsListening(true);
                errorCountRef.current = 0;
              } catch {
                shouldKeepListeningRef.current = false;
                setIsListening(false);
              }
            }
          }, delay);
        } else {
          setIsListening(false);
        }
      };

      recognition.start();
      setIsListening(true);
    } catch (err) {
      console.error('Failed to start speech recognition', err);
      setIsListening(false);
    }
  }, [isSupported, isListening, startAutoSendTimer, clearAutoSendTimer]);

  const stopListening = useCallback(() => {
    // User explicitly stopped - don't auto-restart
    shouldKeepListeningRef.current = false;
    if (!recognitionRef.current || !isListening) return;
    recognitionRef.current.stop();
    setIsListening(false);
    clearAutoSendTimer();
  }, [isListening, clearAutoSendTimer]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    transcriptRef.current = '';
    clearAutoSendTimer();
    // Stop and restart recognition to flush the internal event.results buffer.
    // Without this, onresult rebuilds the full transcript from all accumulated results.
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      // onend handler will auto-restart since shouldKeepListeningRef stays true
    }
  }, [isListening, clearAutoSendTimer]);

  return {
    isListening,
    transcript,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
    error,
  };
};
